import React, { useCallback, useEffect, useMemo, memo } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import {
    OverlayStyledImage,
    LeftChevronWrapper,
    RightChevronWrapper,
    CloseIconWrapper,
    StyledFigure,
    StyledFigcaption,
    BgPictureContainer,
    OverlayVideoWrapper,
    OverlayFooterWrapper,
    OverlayCounter,
    overlayCarouselModalStyles,
    EmblaViewport,
    EmblaContainer,
    EmblaSlide,
} from './styles'
import { LeftChevron, RightChevron, CloseIcon } from '../icons'
import type { OverlayCarouselProps } from './types'
import { Modal, Typography } from 'react-components'
import { useCarousel } from './hooks/use-carousel'

import { getImageUrl } from './get-image-url'
import VideoPlayer from '../../../common/video-player'

const OverlayCarousel: React.FC<OverlayCarouselProps> = ({
    images,
    video,
    setOverlay,
    setSelectedImage,
    isOverlayOpen,
    initialSlide,
    footer,
    onOverlayOpen,
    shouldAutoplayVideo = false,
    getOverlayCounter,
}) => {
    const onSlideChange = useCallback((index: number) => setSelectedImage(index), [setSelectedImage])

    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, startIndex: initialSlide || 0 })
    const { prev, next, currentSlide } = useCarousel(emblaApi, undefined, { onSlideChange })

    // Scroll to initial slide when overlay opens
    useEffect(() => {
        if (isOverlayOpen && emblaApi && initialSlide !== undefined) {
            emblaApi.scrollTo(initialSlide, true)
        }
    }, [isOverlayOpen, emblaApi, initialSlide])

    useEffect(() => {
        if (isOverlayOpen) {
            onOverlayOpen?.()
        }
    }, [isOverlayOpen, onOverlayOpen])

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!isOverlayOpen) {
                return
            }

            switch (event.key) {
                case 'ArrowRight': {
                    next()
                    break
                }
                case 'ArrowLeft': {
                    prev()
                    break
                }
                default: {
                    break
                }
            }
        },
        [isOverlayOpen, next, prev],
    )

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown])

    const closeModal = useCallback(() => {
        setOverlay(false)
    }, [setOverlay])

    const counter = getOverlayCounter?.(currentSlide)

    const renderedImages = useMemo(
        () =>
            images.map((image, index) => {
                const smallImage = getImageUrl('small-mobile', image)
                const mediumImage = getImageUrl('medium-mobile', image)
                const largeImage = getImageUrl('large-mobile', image)
                const desktopImage = getImageUrl('desktop', image)
                const wideScreenImage = getImageUrl('wide-screen', image)
                return (
                    <StyledFigure key={`overlay_modal_image_${index}`}>
                        <BgPictureContainer>
                            {!!wideScreenImage && <source media="(min-width:1024px)" srcSet={wideScreenImage} />}
                            {!!desktopImage && <source media="(min-width:640px)" srcSet={desktopImage} />}
                            {!!largeImage && <source media="(min-width:480px)" srcSet={largeImage} />}
                            {!!mediumImage && <source media="(min-width:320px)" srcSet={mediumImage} />}
                            {!!smallImage && (
                                <OverlayStyledImage loading="lazy" alt={image?.caption} src={smallImage} />
                            )}
                        </BgPictureContainer>
                        {image?.caption && (
                            <StyledFigcaption>
                                <Typography variant="subtitle2" fontWeight={'regular'} color="white">
                                    {image?.caption}
                                </Typography>
                            </StyledFigcaption>
                        )}
                    </StyledFigure>
                )
            }),
        [images],
    )

    return (
        <Modal
            isOpen={isOverlayOpen}
            onRequestClose={closeModal}
            style={overlayCarouselModalStyles}
            ariaHideApp={false}
        >
            {!!counter && <OverlayCounter>{`${counter.current} / ${counter.total}`}</OverlayCounter>}
            <EmblaViewport ref={emblaRef}>
                <EmblaContainer>
                    {video && (
                        <EmblaSlide key="slide_video">
                            <OverlayVideoWrapper>
                                <StyledFigure>
                                    <VideoPlayer
                                        src={video.src}
                                        isActive={currentSlide === 0}
                                        autoplay={shouldAutoplayVideo}
                                    />
                                    {video?.caption && (
                                        <StyledFigcaption>
                                            <Typography variant="subtitle2" fontWeight={'regular'} color="white">
                                                {video?.caption}
                                            </Typography>
                                        </StyledFigcaption>
                                    )}
                                </StyledFigure>
                            </OverlayVideoWrapper>
                        </EmblaSlide>
                    )}
                    {images.map((_image, index) => (
                        <EmblaSlide key={`slide_image_${index}`}>{renderedImages[index]}</EmblaSlide>
                    ))}
                </EmblaContainer>
            </EmblaViewport>
            {footer && (
                <OverlayFooterWrapper>
                    {footer({ onClose: closeModal, activeImageIndex: currentSlide })}
                </OverlayFooterWrapper>
            )}
            <LeftChevronWrapper onClick={prev}>
                <LeftChevron />
            </LeftChevronWrapper>
            <RightChevronWrapper onClick={next}>
                <RightChevron />
            </RightChevronWrapper>
            <CloseIconWrapper onClick={closeModal}>
                <CloseIcon />
            </CloseIconWrapper>
        </Modal>
    )
}

export default memo(OverlayCarousel)
